import React from "react";
import { Formik } from "formik";
import { RegionsValidation } from "./RegionsValidation";
import { Drawer, FormControlLabel, Grid, Switch, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined"
import IconButtonIcons from "../../Common/IconButtonIcons";
import Input from "../../Common/Input";
import Button from "../../Common/Button";
import { useDispatch } from "react-redux";

import { setSnackBar } from "../../../store/common/commonSlice";
import { useAddNewRegionsMutation, useUpdateRegionsMutation } from "../../../store/masters/mastersApis";

export const RegionsAddDrawer = ({ initialValues, show, close, formType }) => {
    const dispatch = useDispatch();
    const [addNewRegion, { isLoading: addRegionLoading }] = useAddNewRegionsMutation();
    const [updateRegion, { isLoading: updateRegionLoading }] = useUpdateRegionsMutation();

    const disabled = formType === 'View' ? true : false;

    const onFormSubmit = async (values, resetForm) => {
        try {
            let payload = { 
                ...values,
                is_active: values.is_active !== false // Ensure boolean value
            };

            if (formType === "Edit") {
                await updateRegion(payload).unwrap();
                dispatch(setSnackBar({
                    open: true,
                    message: "Region updated successfully",
                    severity: "success",
                }))
            } else {
                await addNewRegion(payload).unwrap();
                dispatch(setSnackBar({
                    open: true,
                    message: "Region created successfully",
                    severity: "success",
                }))
            }
            close();
            resetForm();
        } catch (error) {
            dispatch(setSnackBar({
                open: true,
                message: error?.data?.message || error.message,
                severity: "error",
            }))
        }
    };

    return (
        <Formik
            enableReinitialize
            initialValues={{ 
                name: "",
                code: "",
                is_active: true,
                ...initialValues 
            }}
            onSubmit={(values, { resetForm }) => {
                onFormSubmit(values, resetForm);
            }}
            validationSchema={RegionsValidation}
        >
            {({
                handleChange,
                handleBlur,
                handleSubmit,
                values,
                errors,
                setFieldValue
            }) => (
                <Drawer
                    anchor={"right"}
                    open={show}
                    PaperProps={{
                        sx: { width: { xs: '100%', md: '25%', sm: "25%", lg: "25%" } },
                    }}
                    onClose={() => close()}
                >
                    <Grid container sx={{ display: "flex" }} direction={"column"} width={"100%"} height={"100%"} >
                        <Grid container flex={0} px={2} py={1} borderBottom={1} borderColor={"rgba(5, 5, 5, 0.06)"} justifyContent={"space-between"}>
                            <Grid item alignSelf={"center"}>
                                <Typography variant="h6">{formType === "Add" ? "Create new Region" : formType === "Edit" ? "Update Region" : "View Region"}</Typography>
                            </Grid>
                            <Grid item alignSelf={"center"}>
                                <IconButtonIcons color="default" title="Close" IconComponent={CloseIcon} onClick={() => close()} />
                            </Grid>
                        </Grid>
                        <Grid flex={1} px={2} py={2} overflow={"auto"}>
                            <Grid container spacing={2} >
                                {
                                    formType !== 'Add' && (
                                        <Grid item xs={12} md={12}>
                                            <Input
                                                label="ID number"
                                                value={values?.id || ""}
                                                fullWidth
                                                disabled={true}
                                                sx={{ backgroundColor: "#FAFAFA" }}
                                            />
                                        </Grid>
                                    )
                                }
                                <Grid item xs={12} md={12}>
                                    <Input
                                        id='name'
                                        name="name"
                                        label="Region Name"
                                        placeholder="Enter region name (e.g., North, South, Maharashtra)"
                                        onChange={handleChange("name")}
                                        onBlur={handleBlur("name")}
                                        value={values?.name || ""}
                                        error={Boolean(errors.name)}
                                        helperText={errors.name}
                                        fullWidth
                                        disabled={disabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Input
                                        id='code'
                                        name="code"
                                        label="Region Code"
                                        placeholder="Enter region code (e.g., N, S, MH)"
                                        onChange={(e) => {
                                            const value = e.target.value.toUpperCase();
                                            setFieldValue("code", value);
                                        }}
                                        onBlur={handleBlur("code")}
                                        value={values?.code || ""}
                                        error={Boolean(errors.code)}
                                        helperText={errors.code || "Use 2-10 characters, letters and numbers only"}
                                        fullWidth
                                        disabled={disabled}
                                        inputProps={{
                                            style: { textTransform: 'uppercase' },
                                            maxLength: 10
                                        }}
                                    />
                                </Grid>
                                {formType === "Edit" && (
                                    <Grid item xs={12} md={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values?.is_active !== false}
                                                    onChange={(e) => setFieldValue("is_active", e.target.checked)}
                                                    disabled={disabled}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2" sx={{ color: values?.is_active !== false ? '#4CAF50' : '#666' }}>
                                                    {values?.is_active !== false ? 'Active' : 'Inactive'}
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        {
                            formType !== 'View'
                                ?
                                <Grid flexShrink={0} borderTop={1} borderColor={"rgba(152, 188, 252, 0.16)"} sx={{ padding: "8px 16px" }}>
                                    <Grid sx={{ display: "flex", justifyContent: "flex-end" }}>
                                        <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Button size="medium" type="submit" loading={addRegionLoading || updateRegionLoading} onClick={() => handleSubmit()}>
                                                {formType === "Add" ? "Save Region" : "Update Region"}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                :
                                null
                        }
                    </Grid>
                </Drawer>
            )
            }
        </Formik >
    )
}